import React, { CSSProperties, ReactNode, useState } from 'react';
import DialogBase from '../../../base/DialogBase/DialogBase';
import { useAssignLibrariesTagsMutation, useGetTagListWithTypeQuery, useGetTagTypesQuery } from '../../../generated/graphql';
import { FormAutoComplete } from '../../form';
import styles from './AddLibrariesTagsDialog.module.css';

type AddLibrariesTagsDialogProps = {
    onClose: () => void;
    ids: string[];
    visible: boolean;
    style?: CSSProperties;
    children?: ReactNode;
};

const AddLibrariesTagsDialog = ({ ids, style, visible, onClose }: AddLibrariesTagsDialogProps) => {
    const [tagType, setTagType] = useState('');
    const [searchTag, setSearchTag] = useState('');
    const [validationError, setValidationError] = useState('');
    const [assignLibrariesTags, result] = useAssignLibrariesTagsMutation();
    const types = useGetTagTypesQuery();
    const tags = useGetTagListWithTypeQuery({
        variables: { contains: searchTag, type: tagType },
    });

    const handleSubmit = async () => {
        const selectedType = types.data?.TagTypes?.find(type => type === tagType);
        const selectedTag = tags.data?.TagList?.nodes?.find(tag => tag?.name === searchTag);

        if (!tagType || !searchTag) {
            setValidationError('Please fill all of the following fields.');
            return;
        }
        if (!selectedType) {
            setValidationError('Please Select A Valid Type!');
            return;
        }
        if (!selectedTag?.id) {
            setValidationError('Please Select A Valid Tag!');
            return;
        }

        setValidationError('');
        try {
            await assignLibrariesTags({ variables: { ids, tagIds: [selectedTag.id] } });
            onClose();
        } catch {
            // Apollo surfaces the mutation error through `result.error`.
        }
    };

    return (
        <DialogBase
            title="Assign Library Tags"
            type="OkCancel"
            visible={visible}
            onClose={onClose}
            onSubmit={handleSubmit}
            validationError={validationError}
            className={styles.AddLibrariesTagsDialog}
            style={style}
            data-testid="AddLibrariesTagsDialog"
            isLoading={result.loading}
        >
            <FormAutoComplete
                label="Tag Type"
                name="type"
                value={tagType}
                onChange={setTagType}
                options={(types.data?.TagTypes || []).filter((type): type is string => !!type)}
            />
            <FormAutoComplete
                label="Tag"
                name="tag"
                value={searchTag}
                onChange={setSearchTag}
                options={(tags.data?.TagList?.nodes?.map(tag => tag?.name) || []).filter((name): name is string => !!name)}
            />
        </DialogBase>
    );
};

export default AddLibrariesTagsDialog;
